<template>
  <div id="TicketsAdmin" class="container-fluid">
    <!-- Header Row with Action Buttons -->
    <div class="row align-items-center mb-3">
      <div class="col-md-6">
        <div class="pageTitle">Tickets</div>
      </div>
      <div class="col-md-6 text-md-right mt-2 mt-md-0" v-if="authUserRole === 'admin'">
        <button class="btn btn-outline-primary btn-sm mr-2 shadow-sm" @click="openReassignModal(0)">
          <b-icon-arrow-left-right class="mr-1"></b-icon-arrow-left-right> Reassign (Agent to Agent)
        </button>
        <button class="btn btn-primary btn-sm shadow-sm" @click="openReassignModal(1)">
          <b-icon-person-plus class="mr-1"></b-icon-person-plus> Reassign Multiple / Unassigned
        </button>
      </div>
    </div>

    <!-- Tickets List (Preserved without editing List.vue) -->
    <div class="row">
      <div class="col-md-12">
        <List :key="listKey" :routeName="routeName" />
      </div>
    </div>

    <!-- ============================================================= -->
    <!-- MODAL: Reassign Tickets (Tabs: Agent to Agent & Unassigned)   -->
    <!-- ============================================================= -->
    <b-modal
      id="reassignTicketsModal"
      title="Reassign Tickets"
      size="xl"
      hide-footer
      centered
      @shown="onModalShown"
    >
      <b-tabs v-model="activeTab" content-class="mt-3">
        <!-- ========================================================= -->
        <!-- TAB 1: Agent to Agent Reassignment                        -->
        <!-- ========================================================= -->
        <b-tab title="Agent to Agent" active>
          <div class="alert alert-light border py-2 mb-3">
            <b-icon-info-circle class="text-primary mr-1"></b-icon-info-circle>
            <b>Agent to Agent:</b> Select the source agent to view their currently assigned active tickets, choose the destination agent, and reassign all or selected tickets.
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="font-weight-bold">From Agent (Source): <span class="text-danger">*</span></label>
              <select
                class="form-control"
                v-model="fromAgentUUID"
                @change="onFromAgentChange"
                :disabled="loadingAgents"
              >
                <option value="" disabled>-- Select Source Agent --</option>
                <option
                  v-for="agent in agents"
                  :key="'from-' + agent.uuid"
                  :value="agent.uuid"
                >
                  {{ agent.name }} ({{ agent.activeTicketsCount }} active tickets)
                </option>
              </select>
            </div>

            <div class="col-md-6 mt-3 mt-md-0">
              <label class="font-weight-bold">To Agent (Destination): <span class="text-danger">*</span></label>
              <select
                class="form-control"
                v-model="toAgentUUID"
                :disabled="loadingAgents || !fromAgentUUID"
              >
                <option value="" disabled>-- Select Destination Agent --</option>
                <option
                  v-for="agent in filteredToAgents"
                  :key="'to-' + agent.uuid"
                  :value="agent.uuid"
                >
                  {{ agent.name }} ({{ agent.activeTicketsCount }} active tickets)
                </option>
              </select>
            </div>
          </div>

          <!-- Loading state for agent tickets -->
          <div v-if="loadingFromAgentTickets" class="text-center py-4">
            <b-spinner variant="primary" label="Loading agent tickets..."></b-spinner>
            <p class="text-muted mt-2 small">Loading active tickets for this agent...</p>
          </div>

          <!-- If source agent has no tickets -->
          <div
            v-else-if="fromAgentUUID && fromAgentTickets.length === 0"
            class="alert alert-warning text-center py-3"
          >
            <b-icon-check-circle class="mr-1"></b-icon-check-circle>
            This agent currently has no active or open tickets to reassign.
          </div>

          <!-- Tickets Table for Source Agent -->
          <div v-else-if="fromAgentUUID && fromAgentTickets.length > 0">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div class="d-flex align-items-center">
                <div class="custom-control custom-checkbox mr-3">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="selectAllFromAgent"
                    :checked="isAllFromAgentSelected"
                    @change="toggleSelectAllFromAgent"
                  />
                  <label class="custom-control-label font-weight-bold" for="selectAllFromAgent">
                    Select All ({{ filteredFromAgentTickets.length }})
                  </label>
                </div>
                <span class="badge badge-info">
                  Selected: {{ selectedFromAgentTicketRefs.length }} of {{ fromAgentTickets.length }}
                </span>
              </div>

              <div style="width: 250px;">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Filter tickets..."
                  v-model="fromAgentSearch"
                />
              </div>
            </div>

            <div class="table-responsive border rounded table-scroll">
              <table class="table table-hover table-sm mb-0">
                <thead class="thead-light">
                  <tr>
                    <th style="width: 40px;"></th>
                    <th style="width: 130px;">Ref #</th>
                    <!-- <th>Subject / Message</th> -->
                    <th>User / Student</th>
                    <th>Category</th>
                    <th style="width: 100px;">Status</th>
                    <th style="width: 150px;">Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="ticket in filteredFromAgentTickets"
                    :key="'from-ticket-' + ticket.ref"
                    :class="{ 'table-primary': selectedFromAgentTicketRefs.includes(ticket.ref) }"
                    @click="toggleSelectFromAgentTicket(ticket.ref)"
                    style="cursor: pointer;"
                  >
                    <td @click.stop>
                      <div class="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          :id="'from-chk-' + ticket.ref"
                          :value="ticket.ref"
                          v-model="selectedFromAgentTicketRefs"
                        />
                        <label class="custom-control-label" :for="'from-chk-' + ticket.ref"></label>
                      </div>
                    </td>
                    <td>
                      <b class="text-primary">{{ ticket.ref }}</b>
                    </td>
                    <!-- <td>
                      <div class="font-weight-bold">{{ ticket.title || '(No subject)' }}</div>
                      <div class="text-muted small text-truncate" style="max-width: 320px;">
                        {{ stripHtml(ticket.body) }}
                      </div>
                    </td> -->
                    <td>
                      <div>{{ ticket.userName || '—' }}</div>
                      <div class="text-muted small">{{ ticket.userEmail }}</div>
                    </td>
                    <td>
                      <div>{{ ticket.categoryName || '—' }}</div>
                      <div class="text-muted small">{{ ticket.subCategoryName }}</div>
                    </td>
                    <td>
                      <span class="badge badge-primary">{{ ticket.status }}</span>
                    </td>
                    <td class="small text-muted">
                      {{ ticket.createdAt | formatDateTime }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Action Buttons for Tab 1 -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mt-3">
              <div class="small text-muted">
                {{ selectedFromAgentTicketRefs.length }} ticket(s) chosen to be reassigned.
              </div>
              <div>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm mr-2"
                  @click="reassignAllFromAgentTickets"
                  :disabled="reassignSubmitting || !toAgentUUID"
                  title="Reassign every active ticket from this agent"
                >
                  Reassign All ({{ fromAgentTickets.length }}) Active Tickets
                </button>
                <button
                  type="button"
                  class="btn btn-success btn-sm"
                  @click="submitAgentToAgentReassign"
                  :disabled="reassignSubmitting || selectedFromAgentTicketRefs.length === 0 || !toAgentUUID"
                >
                  <b-spinner small v-if="reassignSubmitting" class="mr-1"></b-spinner>
                  <b-icon-arrow-right-circle v-else class="mr-1"></b-icon-arrow-right-circle>
                  Reassign Selected ({{ selectedFromAgentTicketRefs.length }}) Tickets
                </button>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- ========================================================= -->
        <!-- TAB 2: Multiple / Unassigned Tickets                      -->
        <!-- ========================================================= -->
        <b-tab title="Multiple / Unassigned Tickets">
          <div class="alert alert-light border py-2 mb-3">
            <b-icon-info-circle class="text-primary mr-1"></b-icon-info-circle>
            <b>Multiple / Unassigned Tickets:</b> Assign tickets currently without an assigned agent (or bulk assign tickets) directly to a chosen agent.
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <label class="font-weight-bold">Target Agent: <span class="text-danger">*</span></label>
              <select
                class="form-control"
                v-model="batchTargetAgentUUID"
                :disabled="loadingAgents"
              >
                <option value="" disabled>-- Select Agent to Assign To --</option>
                <option
                  v-for="agent in agents"
                  :key="'batch-' + agent.uuid"
                  :value="agent.uuid"
                >
                  {{ agent.name }} ({{ agent.activeTicketsCount }} active tickets)
                </option>
              </select>
            </div>
            <div class="col-md-6 text-md-right align-self-end mt-2 mt-md-0">
              <button
                class="btn btn-outline-secondary btn-sm"
                @click="fetchUnassignedTickets"
                :disabled="loadingUnassigned"
                title="Refresh unassigned tickets"
              >
                <b-icon-arrow-clockwise :class="{ 'spin-icon': loadingUnassigned }"></b-icon-arrow-clockwise> Refresh Unassigned List
              </button>
            </div>
          </div>

          <!-- Loading state for unassigned tickets -->
          <div v-if="loadingUnassigned" class="text-center py-4">
            <b-spinner variant="primary" label="Loading unassigned tickets..."></b-spinner>
            <p class="text-muted mt-2 small">Fetching unassigned tickets...</p>
          </div>

          <!-- If no unassigned tickets exist -->
          <div
            v-else-if="unassignedTickets.length === 0"
            class="alert alert-success text-center py-4"
          >
            <b-icon-check-circle font-scale="2" class="text-success mb-2"></b-icon-check-circle>
            <h5>No Unassigned Tickets Found</h5>
            <p class="text-muted mb-0">All tickets in the system currently have an agent assigned.</p>
          </div>

          <!-- Table of Unassigned Tickets -->
          <div v-else>
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <div class="d-flex align-items-center">
                <div class="custom-control custom-checkbox mr-3">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="selectAllUnassigned"
                    :checked="isAllUnassignedSelected"
                    @change="toggleSelectAllUnassigned"
                  />
                  <label class="custom-control-label font-weight-bold" for="selectAllUnassigned">
                    Select All ({{ filteredUnassignedTickets.length }})
                  </label>
                </div>
                <span class="badge badge-warning text-dark">
                  Unassigned Total: {{ unassignedTickets.length }}
                </span>
                <span class="badge badge-info ml-2">
                  Selected: {{ selectedUnassignedTicketRefs.length }}
                </span>
              </div>

              <div style="width: 250px;">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Filter unassigned tickets..."
                  v-model="unassignedSearch"
                />
              </div>
            </div>

            <div class="table-responsive border rounded table-scroll">
              <table class="table table-hover table-sm mb-0">
                <thead class="thead-light">
                  <tr>
                    <th style="width: 40px;"></th>
                    <th style="width: 130px;">Ref #</th>
                    <!-- <th>Subject / Message</th> -->
                    <th>User / Student</th>
                    <th>Category</th>
                    <th style="width: 100px;">Status</th>
                    <th style="width: 150px;">Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="ticket in filteredUnassignedTickets"
                    :key="'unassigned-ticket-' + ticket.ref"
                    :class="{ 'table-primary': selectedUnassignedTicketRefs.includes(ticket.ref) }"
                    @click="toggleSelectUnassignedTicket(ticket.ref)"
                    style="cursor: pointer;"
                  >
                    <td @click.stop>
                      <div class="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          :id="'unassigned-chk-' + ticket.ref"
                          :value="ticket.ref"
                          v-model="selectedUnassignedTicketRefs"
                        />
                        <label class="custom-control-label" :for="'unassigned-chk-' + ticket.ref"></label>
                      </div>
                    </td>
                    <td>
                      <b class="text-primary">{{ ticket.ref }}</b>
                    </td>
                    <!-- <td>
                      <div class="font-weight-bold">{{ ticket.title || '(No subject)' }}</div>
                      <div class="text-muted small text-truncate" style="max-width: 320px;">
                        {{ stripHtml(ticket.body) }}
                      </div>
                    </td> -->
                    <td>
                      <div>{{ ticket.userName || '—' }}</div>
                      <div class="text-muted small">{{ ticket.userEmail }}</div>
                    </td>
                    <td>
                      <div>{{ ticket.categoryName || '—' }}</div>
                      <div class="text-muted small">{{ ticket.subCategoryName }}</div>
                    </td>
                    <td>
                      <span class="badge badge-danger">{{ ticket.status }}</span>
                    </td>
                    <td class="small text-muted">
                      {{ ticket.createdAt | formatDateTime }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Action Buttons for Tab 2 -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mt-3">
              <div class="small text-muted">
                {{ selectedUnassignedTicketRefs.length }} ticket(s) selected to be assigned.
              </div>
              <div>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm mr-2"
                  @click="assignAllUnassignedTickets"
                  :disabled="batchSubmitting || !batchTargetAgentUUID"
                  title="Assign all unassigned tickets to target agent"
                >
                  Assign All ({{ unassignedTickets.length }}) Unassigned Tickets
                </button>
                <button
                  type="button"
                  class="btn btn-success btn-sm"
                  @click="submitBatchReassign"
                  :disabled="batchSubmitting || selectedUnassignedTicketRefs.length === 0 || !batchTargetAgentUUID"
                >
                  <b-spinner small v-if="batchSubmitting" class="mr-1"></b-spinner>
                  <b-icon-check-circle v-else class="mr-1"></b-icon-check-circle>
                  Assign Selected ({{ selectedUnassignedTicketRefs.length }}) Tickets
                </button>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import List from '@/components/List.vue'

export default {
  name: 'TicketsAdmin',

  data() {
    return {
      listKey: 0,
      activeTab: 0,

      // Agents list
      loadingAgents: false,
      agents: [],

      // Tab 1: Agent to Agent
      fromAgentUUID: '',
      toAgentUUID: '',
      loadingFromAgentTickets: false,
      fromAgentTickets: [],
      selectedFromAgentTicketRefs: [],
      fromAgentSearch: '',
      reassignSubmitting: false,

      // Tab 2: Multiple / Unassigned
      batchTargetAgentUUID: '',
      loadingUnassigned: false,
      unassignedTickets: [],
      selectedUnassignedTicketRefs: [],
      unassignedSearch: '',
      batchSubmitting: false
    }
  },

  components: {
    List
  },

  computed: {
    authUserRole() {
      return this.$store.state.authUser.Role
    },

    routeName() {
      if (this.$store.state.authUser.Role === 'admin') {
        return 'TicketDetailsAdmin'
      } else {
        return 'TicketDetailsManager'
      }
    },

    filteredToAgents() {
      if (!this.fromAgentUUID) return this.agents
      return this.agents.filter(a => a.uuid !== this.fromAgentUUID)
    },

    filteredFromAgentTickets() {
      if (!this.fromAgentSearch || this.fromAgentSearch.trim() === '') {
        return this.fromAgentTickets
      }
      const q = this.fromAgentSearch.toLowerCase().trim()
      return this.fromAgentTickets.filter(t => {
        return (
          (t.ref && t.ref.toLowerCase().includes(q)) ||
          (t.title && t.title.toLowerCase().includes(q)) ||
          (t.userName && t.userName.toLowerCase().includes(q)) ||
          (t.categoryName && t.categoryName.toLowerCase().includes(q)) ||
          (t.subCategoryName && t.subCategoryName.toLowerCase().includes(q))
        )
      })
    },

    isAllFromAgentSelected() {
      if (this.filteredFromAgentTickets.length === 0) return false
      return this.filteredFromAgentTickets.every(t =>
        this.selectedFromAgentTicketRefs.includes(t.ref)
      )
    },

    filteredUnassignedTickets() {
      if (!this.unassignedSearch || this.unassignedSearch.trim() === '') {
        return this.unassignedTickets
      }
      const q = this.unassignedSearch.toLowerCase().trim()
      return this.unassignedTickets.filter(t => {
        return (
          (t.ref && t.ref.toLowerCase().includes(q)) ||
          (t.title && t.title.toLowerCase().includes(q)) ||
          (t.userName && t.userName.toLowerCase().includes(q)) ||
          (t.categoryName && t.categoryName.toLowerCase().includes(q)) ||
          (t.subCategoryName && t.subCategoryName.toLowerCase().includes(q))
        )
      })
    },

    isAllUnassignedSelected() {
      if (this.filteredUnassignedTickets.length === 0) return false
      return this.filteredUnassignedTickets.every(t =>
        this.selectedUnassignedTicketRefs.includes(t.ref)
      )
    }
  },

  methods: {
    openReassignModal(tabIndex) {
      this.activeTab = tabIndex
      this.$bvModal.show('reassignTicketsModal')
    },

    onModalShown() {
      this.fetchAgents()
      this.fetchUnassignedTickets()
    },

    fetchAgents() {
      this.loadingAgents = true
      axios
        .get('/admin/tickets/reassign-agents')
        .then(res => {
          if (res.data.status === 'success') {
            this.agents = res.data.agents || []
          }
          this.loadingAgents = false
        })
        .catch(() => {
          this.loadingAgents = false
        })
    },

    fetchUnassignedTickets() {
      this.loadingUnassigned = true
      axios
        .get('/admin/tickets/unassigned')
        .then(res => {
          if (res.data.status === 'success') {
            this.unassignedTickets = res.data.tickets || []
          }
          this.loadingUnassigned = false
        })
        .catch(() => {
          this.loadingUnassigned = false
        })
    },

    // -------------------------------------------------------------
    // Tab 1: Agent to Agent Methods
    // -------------------------------------------------------------
    onFromAgentChange() {
      this.selectedFromAgentTicketRefs = []
      this.fromAgentTickets = []
      if (!this.fromAgentUUID) return

      // If toAgent was set to the same agent, clear it
      if (this.toAgentUUID === this.fromAgentUUID) {
        this.toAgentUUID = ''
      }

      this.loadingFromAgentTickets = true
      axios
        .get(`/admin/tickets/by-agent/${this.fromAgentUUID}`)
        .then(res => {
          if (res.data.status === 'success') {
            this.fromAgentTickets = res.data.tickets || []
            // By default, select all tickets for user convenience
            this.selectedFromAgentTicketRefs = this.fromAgentTickets.map(t => t.ref)
          } else {
            this.$swal('Oops!', res.data.message, 'error')
          }
          this.loadingFromAgentTickets = false
        })
        .catch(() => {
          this.loadingFromAgentTickets = false
          this.$swal('Oops!', 'Failed to fetch tickets for this agent.', 'error')
        })
    },

    toggleSelectFromAgentTicket(ref) {
      const idx = this.selectedFromAgentTicketRefs.indexOf(ref)
      if (idx > -1) {
        this.selectedFromAgentTicketRefs.splice(idx, 1)
      } else {
        this.selectedFromAgentTicketRefs.push(ref)
      }
    },

    toggleSelectAllFromAgent() {
      if (this.isAllFromAgentSelected) {
        // Deselect currently filtered tickets
        const filteredRefs = this.filteredFromAgentTickets.map(t => t.ref)
        this.selectedFromAgentTicketRefs = this.selectedFromAgentTicketRefs.filter(
          ref => !filteredRefs.includes(ref)
        )
      } else {
        // Select all filtered tickets
        const newSet = new Set([
          ...this.selectedFromAgentTicketRefs,
          ...this.filteredFromAgentTickets.map(t => t.ref)
        ])
        this.selectedFromAgentTicketRefs = Array.from(newSet)
      }
    },

    submitAgentToAgentReassign() {
      if (!this.fromAgentUUID || !this.toAgentUUID) {
        this.$swal('Required', 'Please select both source and destination agents.', 'warning')
        return
      }

      if (this.selectedFromAgentTicketRefs.length === 0) {
        this.$swal('Required', 'Please select at least one ticket to reassign.', 'warning')
        return
      }

      const fromAgent = this.agents.find(a => a.uuid === this.fromAgentUUID)
      const toAgent = this.agents.find(a => a.uuid === this.toAgentUUID)
      const count = this.selectedFromAgentTicketRefs.length

      this.$swal({
        title: 'Confirm Reassignment',
        text: `Reassign ${count} ticket(s) from "${fromAgent ? fromAgent.name : 'source'}" to "${toAgent ? toAgent.name : 'destination'}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Reassign',
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (result.isConfirmed) {
          this.executeAgentToAgentReassign(this.selectedFromAgentTicketRefs)
        }
      })
    },

    reassignAllFromAgentTickets() {
      if (!this.fromAgentUUID || !this.toAgentUUID) {
        this.$swal('Required', 'Please select both source and destination agents.', 'warning')
        return
      }

      const fromAgent = this.agents.find(a => a.uuid === this.fromAgentUUID)
      const toAgent = this.agents.find(a => a.uuid === this.toAgentUUID)

      this.$swal({
        title: 'Confirm All Reassignment',
        text: `Reassign ALL active tickets from "${fromAgent ? fromAgent.name : 'source'}" to "${toAgent ? toAgent.name : 'destination'}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Reassign All',
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (result.isConfirmed) {
          // Pass empty array to signal backend to reassign all active
          this.executeAgentToAgentReassign([])
        }
      })
    },

    executeAgentToAgentReassign(ticketRefs) {
      this.reassignSubmitting = true
      const formData = new FormData()
      formData.append('fromAgentUUID', this.fromAgentUUID)
      formData.append('toAgentUUID', this.toAgentUUID)
      if (ticketRefs && ticketRefs.length > 0) {
        formData.append('ticketRefs', JSON.stringify(ticketRefs))
      }

      axios
        .post('/admin/tickets/reassign-agent-to-agent', formData)
        .then(res => {
          if (res.data.status === 'success') {
            this.$swal('Success!', res.data.message, 'success')
            this.$bvModal.hide('reassignTicketsModal')
            // Refresh table list
            this.listKey++
          } else {
            this.$swal('Oops!', res.data.message, 'error')
          }
          this.reassignSubmitting = false
        })
        .catch(err => {
          this.reassignSubmitting = false
          this.$swal('Oops!', 'Failed to reassign tickets.', 'error')
        })
    },

    // -------------------------------------------------------------
    // Tab 2: Multiple / Unassigned Tickets Methods
    // -------------------------------------------------------------
    toggleSelectUnassignedTicket(ref) {
      const idx = this.selectedUnassignedTicketRefs.indexOf(ref)
      if (idx > -1) {
        this.selectedUnassignedTicketRefs.splice(idx, 1)
      } else {
        this.selectedUnassignedTicketRefs.push(ref)
      }
    },

    toggleSelectAllUnassigned() {
      if (this.isAllUnassignedSelected) {
        const filteredRefs = this.filteredUnassignedTickets.map(t => t.ref)
        this.selectedUnassignedTicketRefs = this.selectedUnassignedTicketRefs.filter(
          ref => !filteredRefs.includes(ref)
        )
      } else {
        const newSet = new Set([
          ...this.selectedUnassignedTicketRefs,
          ...this.filteredUnassignedTickets.map(t => t.ref)
        ])
        this.selectedUnassignedTicketRefs = Array.from(newSet)
      }
    },

    submitBatchReassign() {
      if (!this.batchTargetAgentUUID) {
        this.$swal('Required', 'Please select a target agent.', 'warning')
        return
      }

      if (this.selectedUnassignedTicketRefs.length === 0) {
        this.$swal('Required', 'Please select at least one ticket to assign.', 'warning')
        return
      }

      const toAgent = this.agents.find(a => a.uuid === this.batchTargetAgentUUID)
      const count = this.selectedUnassignedTicketRefs.length

      this.$swal({
        title: 'Confirm Assignment',
        text: `Assign ${count} ticket(s) to "${toAgent ? toAgent.name : 'agent'}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Assign',
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (result.isConfirmed) {
          this.executeBatchAssign(this.selectedUnassignedTicketRefs, false)
        }
      })
    },

    assignAllUnassignedTickets() {
      if (!this.batchTargetAgentUUID) {
        this.$swal('Required', 'Please select a target agent.', 'warning')
        return
      }

      const toAgent = this.agents.find(a => a.uuid === this.batchTargetAgentUUID)

      this.$swal({
        title: 'Confirm All Assignment',
        text: `Assign ALL (${this.unassignedTickets.length}) unassigned tickets to "${toAgent ? toAgent.name : 'agent'}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, Assign All',
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (result.isConfirmed) {
          this.executeBatchAssign([], true)
        }
      })
    },

    executeBatchAssign(ticketRefs, allUnassigned) {
      this.batchSubmitting = true
      const formData = new FormData()
      formData.append('toAgentUUID', this.batchTargetAgentUUID)
      if (allUnassigned) {
        formData.append('allUnassigned', 'true')
      } else if (ticketRefs && ticketRefs.length > 0) {
        formData.append('ticketRefs', JSON.stringify(ticketRefs))
      }

      axios
        .post('/admin/tickets/reassign-batch', formData)
        .then(res => {
          if (res.data.status === 'success') {
            this.$swal('Success!', res.data.message, 'success')
            this.$bvModal.hide('reassignTicketsModal')
            // Refresh table list
            this.listKey++
          } else {
            this.$swal('Oops!', res.data.message, 'error')
          }
          this.batchSubmitting = false
        })
        .catch(err => {
          this.batchSubmitting = false
          this.$swal('Oops!', 'Failed to assign tickets.', 'error')
        })
    },

    stripHtml(html) {
      if (!html) return ''
      const doc = new DOMParser().parseFromString(html, 'text/html')
      return doc.body.textContent || ''
    }
  }
}
</script>

<style scoped>
.pageTitle {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.table-scroll {
  max-height: 420px;
  overflow-y: auto;
}

.table-sm td,
.table-sm th {
  padding: 0.5rem;
  vertical-align: middle;
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}
</style>
